import { Link } from "react-router-dom";

import { useChrome } from "./ChromeContext";
import { NotificationsBell } from "./NotificationsBell";
import { RepoSwitcher } from "./RepoSwitcher";
import { TaskTitle } from "./TaskTitle";
import { TinsmithLogo } from "./TinsmithLogo";
import styles from "./TopBar.module.css";
import { useSession } from "../auth/session";
import { Avatar } from "../components/Avatar";
import { Button, ButtonLink } from "../components/Button";
import { Kbd } from "../components/Kbd";
import { Menu, MenuContent, MenuItem, MenuTrigger } from "../components/Menu";
import { Pill } from "../components/Pill";
import { Tooltip } from "../components/Tooltip";
import { renameTask, useTask } from "../data/api/tasks";
import { usePresence } from "../data/realtime/usePresence";
import { Icon } from "../icons";
import { cx } from "../lib/cx";
import { statusLabel } from "../lib/time";
import { paths } from "../routing/paths";
import { useRouteInfo } from "../routing/useNav";

export const TopBar = () => {
  const route = useRouteInfo();
  const { openPalette } = useChrome();
  const { signOut, user } = useSession();
  const displayName = user?.display_name ?? "You";

  // The route carries the label; resolving it to a task needs the repository
  // the same route names (`data/api/tasks.ts` §useTask).
  const { refetch, task } = useTask(
    route.name === "task" ? (route.repoId ?? null) : null,
    route.name === "task" ? (route.taskLabel ?? null) : null,
  );
  const inTask = route.name === "task" && Boolean(task);
  const showSwitcher = Boolean(route.repoId) || inTask;
  // Inside a repository the global links give way to the repo context; the
  // Tinsmith logo is the way back out to the global area.
  const inRepo = route.repoId !== undefined;

  // Who else is looking at this task right now (excluding you) — the "…are
  // also viewing" strip (realtime.md §Presence).
  // Keyed on the resolved id, not the label off the route: `presence:task:X`
  // peels to `task:X`, which the server authorises by looking `X` up as a
  // `tasks.public_id`. A label would simply fail to authorise, and presence
  // would go quiet without saying why.
  const others = usePresence(task ? `presence:task:${task.id}` : null).filter(
    (v) => v.user_public_id !== user?.public_id,
  );

  return (
    <header className={styles.topbar}>
      <div className={styles.left}>
        <TinsmithLogo href={paths.dashboard()} />
        {/* Each label is wrapped so a phone can drop it and leave the icon: the
            triad is titled as well as labelled, so an icon-only link is still
            named for a screen reader and a long press. */}
        {inRepo ? null : (
          <nav className={styles.nav}>
            <Link
              className={cx(
                styles.navItem,
                route.name === "dashboard" && styles.active,
              )}
              title="Repositories"
              to={paths.dashboard()}
            >
              <Icon.Repo size={14} />{" "}
              <span className={styles.navLabel}>Repositories</span>
            </Link>
            <Link
              className={cx(
                styles.navItem,
                route.name === "activity" && styles.active,
              )}
              title="Activity"
              to={paths.activity()}
            >
              <Icon.Kanban size={14} />{" "}
              <span className={styles.navLabel}>Activity</span>
            </Link>
            <Link
              className={cx(
                styles.navItem,
                route.name === "runners" && styles.active,
              )}
              title="Runners"
              to={paths.runners()}
            >
              <Icon.Cpu size={14} />{" "}
              <span className={styles.navLabel}>Runners</span>
            </Link>
          </nav>
        )}
        {showSwitcher ? <span className={styles.divider} /> : null}
        {showSwitcher ? <RepoSwitcher currentRepoId={route.repoId} /> : null}
        {inTask && task ? (
          <>
            <span className={styles.crumbSep}>
              <Icon.Chevron size={12} />
            </span>
            <span className={styles.crumb}>{task.displayId}</span>
            <Pill className={styles.status} status={task.status}>
              {statusLabel(task.status)}
            </Pill>
            <TaskTitle
              onRename={(title) => {
                void renameTask(task.id, title)
                  .then(refetch)
                  .catch(() => refetch());
              }}
              title={task.title}
            />
            {others.length > 0 ? (
              <span
                className={styles.presence}
                title={`${others.map((v) => v.name).join(", ")} ${others.length === 1 ? "is" : "are"} also viewing`}
              >
                {others.slice(0, 3).map((v) => (
                  <Avatar key={v.user_public_id} name={v.name} ring size={22} />
                ))}
              </span>
            ) : null}
          </>
        ) : null}
      </div>
      <div className={styles.right}>
        {/* An icon at every width, with what it does and what it answers to on
            the tooltip. The `aria-label` is the button's name whether or not a
            pointer ever hovers it — a phone gets no tooltip. */}
        <Tooltip
          content={
            <>
              Search or run a command
              <Kbd className={styles.searchKbd} inverse>
                ⌘K
              </Kbd>
            </>
          }
          side="bottom"
        >
          <Button
            aria-label="Search or run a command"
            icon
            onClick={openPalette}
            variant="ghost"
          >
            <Icon.Search size={15} />
          </Button>
        </Tooltip>
        <NotificationsBell />
        {/* Dropped on a phone rather than squeezed: the user menu beside it
            carries the same link, so nothing becomes unreachable. */}
        <ButtonLink
          className={styles.settingsLink}
          icon
          title="Settings"
          to={paths.settings()}
          variant="ghost"
        >
          <Icon.Cog size={15} />
        </ButtonLink>
        <Menu>
          <MenuTrigger asChild>
            <button
              className={styles.userButton}
              title={displayName}
              type="button"
            >
              <Avatar name={displayName} size={26} />
            </button>
          </MenuTrigger>
          <MenuContent align="end">
            <div className={styles.userCard}>
              <div className={styles.userName}>{displayName}</div>
              {user?.email ? (
                <div className={styles.userEmail}>{user.email}</div>
              ) : null}
            </div>
            <MenuItem href={paths.settings()}>
              <Icon.Cog size={13} /> Settings
            </MenuItem>
            <MenuItem onSelect={() => void signOut()}>
              <Icon.Power size={13} /> Sign out
            </MenuItem>
          </MenuContent>
        </Menu>
      </div>
    </header>
  );
};
